import { useStrictLocalization } from "@faire/localization/react/useStrictLocalization";
import { IText } from "@faire/slate/private/shared/types";
import React from "react";

import { MessagingBannerVariant } from "./types";

type IProps = {
  action?: React.ReactNode;
  description?: IText;
  label?: string;
  variant: MessagingBannerVariant;
};

/**
 * Renders the label and (optional) body inside a messaging banner. Prepends a
 * visually hidden "Warning:" / "Error:" prefix for `warning` / `critical`
 * variants so screen readers announce the severity.
 */
export const MessagingBannerContent: React.FC<IProps> = ({
  action,
  description,
  label,
  variant,
}) => {
  const { strictLocalize } = useStrictLocalization();

  const warningPrefix = strictLocalize({
    id: "/WYhJM",
    defaultMessage: "Warning:",
    description: {
      text: "Visually hidden prefix announced by screen readers before the label of a warning messaging banner.",
    },
  });

  const errorPrefix = strictLocalize({
    id: "bIj8oH",
    defaultMessage: "Error:",
    description: {
      text: "Visually hidden prefix announced by screen readers before the label of a critical messaging banner.",
    },
  });

  const announcedPrefix = getAnnouncedPrefix({
    variant,
    warningPrefix,
    errorPrefix,
  });

  return (
    <div className="fs-messaging-banner__content">
      {announcedPrefix ? (
        <span className="sr-only">{announcedPrefix} </span>
      ) : null}
      {label ? (
        <p className="fs-messaging-banner__label">
          {label}
        </p>
      ) : null}
      {description || action ? (
        <div className="fs-messaging-banner__body">
          {description ? (
            <p className="type-fs-paragraph">{description}</p>
          ) : null}
          {action}
        </div>
      ) : null}
    </div>
  );
};

const getAnnouncedPrefix = ({
  variant,
  warningPrefix,
  errorPrefix,
}: {
  variant: MessagingBannerVariant;
  warningPrefix: string;
  errorPrefix: string;
}): string | null => {
  if (variant === "warning") {
    return warningPrefix;
  }
  if (variant === "critical") {
    return errorPrefix;
  }
  return null;
};
